import 'reflect-metadata'
import {inject, injectable} from 'inversify'
import {action, makeAutoObservable, observable} from 'mobx'
import { error } from 'dna-common'
import {storage} from '@/services/storage'
import {ROUTES} from '@/router/routes'
import {getRouterStore} from '@/router/utils'
import {openErrorNotification} from '@/utils'
import {createPassword} from '../api'
import {SIGNUP_STORAGE_KEYS} from '../constants'
import {
    ICreatePasswordStore,
    IValidateOtpCodeStore,
    IValidateRefCodeStore,
    ValidateOtpCodeStoreSymbol,
    ValidateRefCodeStoreSymbol
} from '~/code/pages/signUp'

@injectable()
export class CreatePasswordStore implements ICreatePasswordStore {
    isLoading: boolean = false
    validateOtpCodeStore: IValidateOtpCodeStore
    validateRefCodeStore: IValidateRefCodeStore
    isRegistrationSuccessfulModalOpen: boolean = false
    password: string = ''
    restrictionErrors = {
        betweenEightAndFiftyCharacters: true,
        atLeastOneDigit: true,
        atLeastOneSpecialCharacter: true,
        atLeastOneUppercaseLetter: true,
        atLeastOneLowercaseLetter: true,
        onlyLatinCharacters: true
    }

    constructor(
        @inject(ValidateOtpCodeStoreSymbol) validateOtpCodeStore: IValidateOtpCodeStore,
        @inject(ValidateRefCodeStoreSymbol) validateRefCodeStore: IValidateRefCodeStore
    ) {
        makeAutoObservable(this, {
            isLoading: observable,
            createPassword: action.bound
        })

        this.validateOtpCodeStore = validateOtpCodeStore
        this.validateRefCodeStore = validateRefCodeStore
    }

    get verificationId(): string {
        return (
            this.validateOtpCodeStore.verificationId ||
            storage.get(SIGNUP_STORAGE_KEYS.VERIFICATION_ID) ||
            ''
        )
    }

    get refCode(): string {
        return this.validateRefCodeStore.validRef || storage.get(SIGNUP_STORAGE_KEYS.REF_CODE) || ''
    }

    get email(): string {
        return this.validateOtpCodeStore.email
    }

    createPassword = async () => {
        try {
            this.isLoading = true

            const {result, error} = await createPassword({
                password: this.password,
                verificationId: this.verificationId,
                ref: this.refCode,
                email: this.email
            })

            if (error) {
                openErrorNotification(error.message)
                return
            }

            if (result) {
                this.isRegistrationSuccessfulModalOpen = true

                this.clearSignupStorage()
            }
        } catch (err) {
            error(err?.message || err)
        } finally {
            this.isLoading = false
        }
    }

    setPassword = (password: string) => {
        this.password = password
        this.restrictionErrors = {
            betweenEightAndFiftyCharacters: password.length < 8 || password.length > 50,
            atLeastOneDigit: !/\d/.test(password),
            atLeastOneSpecialCharacter: !/[^a-zA-Z0-9]/.test(password),
            atLeastOneUppercaseLetter: !/[A-Z]/.test(password),
            atLeastOneLowercaseLetter: !/[a-z]/.test(password),
            onlyLatinCharacters:
                password.length === 0 || !/[a-zA-Z]/.test(password) || /[^\x20-\x7E]/.test(password)
        }
    }

    checkSignupStepValidity = () => {
        if (!Boolean(this.email) || !Boolean(this.refCode) || !Boolean(this.verificationId)) {
            getRouterStore().push(ROUTES.signUpValidateEmail)
        }
    }

    goToLoginPage = () => {
        getRouterStore().push(ROUTES.login)
    }

    clearSignupStorage = () => {
        Object.values(SIGNUP_STORAGE_KEYS).forEach((key) => {
            storage.set(key, null)
        })
    }
}
